import { TailgateGridTestJobResult } from '../hooks/useTailgateGridTestJob'
import { useTranslation } from 'react-i18next'

import type { ColorScheme } from '@hivewatch/dark'

import {
  GridAnalysisJob,
  TailgateGridTestFailureReason,
} from '@/graphql/generated/schemas'

import { IncidentOptionI } from '../../types/types'
import { GridTestErrorCard } from './GridTestErrorCard'
import { GridTestSuccessCard } from './GridTestSuccessCard'
import { TailgateGridTestSuccessCard } from './TailgateGridTestSuccessCard'

const TAILGATE_FAILURE_DETAIL_KEY = {
  [TailgateGridTestFailureReason.ClipDownloadFailed]:
    'tailgate:grid_test_v2.failure_clip_download',
  [TailgateGridTestFailureReason.EvaluationFailed]:
    'tailgate:grid_test_v2.failure_evaluation',
  [TailgateGridTestFailureReason.ResultDecodeFailed]:
    'tailgate:grid_test_v2.failure_result_decode',
  [TailgateGridTestFailureReason.SidecarTooLarge]:
    'tailgate:grid_test_v2.failure_sidecar_too_large',
  [TailgateGridTestFailureReason.SidecarUploadFailed]:
    'tailgate:grid_test_v2.failure_sidecar_upload',
  [TailgateGridTestFailureReason.Timeout]:
    'tailgate:grid_test_v2.job_timed_out',
} as const satisfies Record<TailgateGridTestFailureReason, string>

interface ResultsStepIProps {
  colorScheme?: ColorScheme
  variant: 'success' | 'error'
  isTailgateExperience?: boolean
  job?: GridAnalysisJob
  labeledIn?: number
  labeledOut?: number
  isSaving?: boolean
  isTimeout?: boolean
  onClose: () => void
  onSelectAnotherIncident: () => void
  onSaveGrid?: () => void
  onRetry?: () => void
  selectedIncident: IncidentOptionI | null
  tailgateJob?: TailgateGridTestJobResult
}

export const ResultsStep = ({
  colorScheme,
  variant,
  isTailgateExperience = false,
  job,
  labeledIn = 0,
  labeledOut = 0,
  isSaving = false,
  isTimeout = false,
  onClose,
  onSelectAnotherIncident,
  onSaveGrid,
  onRetry,
  selectedIncident,
  tailgateJob,
}: ResultsStepIProps) => {
  const { t } = useTranslation()

  if (variant === 'error') {
    const tailgateFailureReason = tailgateJob?.failureReason
    const didTailgateTimeOut =
      isTailgateExperience &&
      (isTimeout ||
        tailgateFailureReason === TailgateGridTestFailureReason.Timeout)
    const tailgateFailureDetail = isTailgateExperience
      ? t(
          didTailgateTimeOut
            ? TAILGATE_FAILURE_DETAIL_KEY[TailgateGridTestFailureReason.Timeout]
            : tailgateFailureReason
            ? TAILGATE_FAILURE_DETAIL_KEY[tailgateFailureReason]
            : 'tailgate:grid_test_v2.job_failed'
        )
      : undefined

    return (
      <GridTestErrorCard
        colorScheme={colorScheme}
        detail={tailgateFailureDetail}
        isTimeout={isTailgateExperience ? didTailgateTimeOut : isTimeout}
        onClose={onClose}
        onRetry={onRetry}
        onSelectAnotherIncident={onSelectAnotherIncident}
        selectedIncident={selectedIncident}
      />
    )
  }

  if (isTailgateExperience && tailgateJob) {
    return (
      <TailgateGridTestSuccessCard
        colorScheme={colorScheme}
        isEnabled={isTailgateExperience}
        isSaving={isSaving}
        job={tailgateJob}
        onClose={onClose}
        onSaveGrid={onSaveGrid}
        onSelectAnotherIncident={onSelectAnotherIncident}
        selectedIncident={selectedIncident}
      />
    )
  }

  // The success branch requires `job` and `onSaveGrid`; GridTestingZone only
  // renders variant='success' when the job has completed, so these are
  // guaranteed at this point.
  return (
    <GridTestSuccessCard
      colorScheme={colorScheme}
      isSaving={isSaving}
      job={job}
      labeledIn={labeledIn}
      labeledOut={labeledOut}
      onClose={onClose}
      onSaveGrid={onSaveGrid}
      onSelectAnotherIncident={onSelectAnotherIncident}
      selectedIncident={selectedIncident}
    />
  )
}
